import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ScanFace, Upload, Wallet, Camera } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { EventCard } from "@/pages/Events";

const HERO = "https://images.unsplash.com/photo-1745790289741-12a211a8325d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";
const STEPS = [
  [Camera, "Fotografer mengunggah", "Ratusan foto event diunggah. Sistem otomatis mendeteksi setiap wajah di dalamnya."],
  [ScanFace, "Kamu unggah swafoto", "Satu swafoto cukup. Kami mencocokkan wajahmu dengan semua foto dalam hitungan detik."],
  [Wallet, "Bayar & unduh HD", "Transfer ke rekening fotografer, unggah bukti, lalu unduh foto asli tanpa watermark."],
];

export default function Landing() {
  const [events, setEvents] = useState([]);
  useEffect(() => { api.get("/events").then((r) => setEvents(r.data.slice(0, 3))).catch(() => {}); }, []);

  return (
    <main data-testid="landing-page">
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 sm:pt-24 pb-20 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-6 fade-up">
          <p className="inline-flex items-center gap-2 rounded-full border border-zinc-200 bg-white px-3 py-1 text-xs font-semibold text-zinc-600 mb-8">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> Pengenalan wajah otomatis
          </p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-zinc-900 leading-[1.02]">
            Temukan dirimu di<br />setiap <span className="text-blue-600">momen.</span>
          </h1>
          <p className="mt-6 text-base md:text-lg text-zinc-600 max-w-lg leading-relaxed">
            Lari pagi, wisuda, konser, atau pernikahan. Unggah satu swafoto dan Akudifoto menemukan semua foto yang memuat wajahmu dari para fotografer.
          </p>
          <div className="mt-10 flex flex-wrap gap-3">
            <Button asChild size="lg" className="rounded-xl bg-blue-600 hover:bg-blue-700 h-12 px-6" data-testid="hero-search-button">
              <Link to="/cari"><ScanFace className="mr-2 h-5 w-5" /> Cari Foto Wajah</Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="rounded-xl h-12 px-6 bg-white" data-testid="hero-photographer-button">
              <Link to="/register?role=photographer"><Upload className="mr-2 h-4 w-4" /> Saya Fotografer</Link>
            </Button>
          </div>
        </div>
        <div className="lg:col-span-6 relative fade-up" style={{ animationDelay: "120ms" }}>
          <div className="relative rounded-3xl overflow-hidden border border-zinc-200 shadow-[0_30px_80px_-30px_rgba(0,0,0,.25)] grain">
            <img src={HERO} alt="Pelari di event" className="w-full aspect-[5/4] object-cover" />
            <div className="absolute left-[38%] top-[18%] w-[24%] aspect-square">
              <span className="corner tl" /><span className="corner tr" /><span className="corner bl" /><span className="corner br" />
              <div className="scan-line" />
            </div>
          </div>
          <div className="absolute -bottom-6 left-6 rounded-2xl bg-white border border-zinc-200 px-5 py-4 shadow-lg flex items-center gap-3">
            <ScanFace className="h-8 w-8 text-blue-600" />
            <div><p className="text-xs text-zinc-500">Wajah cocok</p><p className="font-display font-extrabold text-zinc-900">24 foto ditemukan</p></div>
          </div>
        </div>
      </section>

      <section className="border-y border-zinc-200 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 grid md:grid-cols-3 gap-10">
          {STEPS.map(([Icon, title, desc], i) => (
            <div key={title} className="fade-up" style={{ animationDelay: `${i * 100}ms` }} data-testid={`how-step-${i + 1}`}>
              <div className="flex items-center gap-3 mb-4">
                <span className="font-mono-x text-xs text-zinc-400">0{i + 1}</span>
                <Icon className="h-5 w-5 text-blue-600" />
              </div>
              <h3 className="text-lg font-bold text-zinc-900 mb-2">{title}</h3>
              <p className="text-sm text-zinc-600 leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="flex items-end justify-between mb-10">
          <div>
            <p className="text-xs uppercase tracking-widest text-zinc-400 font-bold mb-2">Terbaru</p>
            <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight">Event pilihan</h2>
          </div>
          <Link to="/events" className="text-sm font-semibold text-blue-600 inline-flex items-center gap-1 hover:gap-2 transition-[gap]" data-testid="landing-all-events-link">Semua event <ArrowRight className="h-4 w-4" /></Link>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {events.map((e) => <EventCard key={e.id} event={e} />)}
          {events.length === 0 && <p className="text-zinc-500">Belum ada event.</p>}
        </div>
      </section>

      <footer className="border-t border-zinc-200 py-10 text-center text-sm text-zinc-500">© 2026 Akudifoto · Jual-beli foto berbasis wajah</footer>
    </main>
  );
}
